<template>
  <a-modal v-if="visible" :title="modalTitle" :visible="visible" :destroy-on-close="true" @cancel="closeModal">
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-2 flex-column"
      :model="form"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex gap-2 flex-column">
        <div class="grid">
          <b class="col-6">Selection by Associates</b>
          <div class="col-6 flex justify-content-end">
            <a-radio-group v-model="associateFilterType" button-style="solid">
              <a-radio-button value="all">All</a-radio-button>
              <a-radio-button value="specific">Specific</a-radio-button>
            </a-radio-group>
          </div>
        </div>

        <div v-show="associateFilterType === 'specific'">
          <a-form-model-item :colon="false" label-align="left" label="Associates">
            <search-associates :multiply="true" :is-associate-id="true" :value.sync="form.associates" />
          </a-form-model-item>
        </div>

        <div v-show="associateFilterType === 'all'">
          <div class="flex gap-2 flex-column">
            <a-form-model-item :colon="false" label-align="left" label="Payment Types">
              <custom-select
                :multiple="true"
                :value.sync="paymentTypeProxy"
                placeholder="Payment Types"
                :options="paymentTypes"
                name="name"
                optionKey="id"
              />
            </a-form-model-item>

            <a-form-model-item :colon="false" label-align="left" label="Positions">
              <custom-select
                :multiple="true"
                :value.sync="roleProxy"
                placeholder="Position"
                :options="roles"
                name="name"
                optionKey="id"
              />
            </a-form-model-item>
            <a-form-model-item
              label=" "
              :colon="false"
              label-align="left"
              help="By default, you select the list of entities that will be
              targeted. By using this option, you can target all entities
              except the ones selected in the box."
            >
              <a-checkbox :checked="excludeRolesMode" @change="handleExcludeRolesModeChange">
                Target All Except Selected
              </a-checkbox>
            </a-form-model-item>

            <a-form-model-item :colon="false" label-align="left" label="Legal Entities">
              <custom-select
                :multiple="true"
                :value.sync="agencyProxy"
                placeholder="Legal Entities"
                :options="legalEntities"
                name="name"
                optionKey="id"
              />
            </a-form-model-item>
            <a-form-model-item
              label=" "
              :colon="false"
              label-align="left"
              help="By default, you select the list of entities that will be
              targeted. By using this option, you can target all entities
              except the ones selected in the box."
            >
              <a-checkbox :checked="excludeLegalEntitiesMode" @change="handleExcludeLegalEntitiesModeChange">
                Target All Except Selected
              </a-checkbox>
            </a-form-model-item>

            <a-form-model-item
              v-if="hasContractorPaymentType"
              :colon="false"
              label-align="left"
              label="Contractor Types"
            >
              <custom-select
                :multiple="true"
                :value.sync="contractorSubTypeProxy"
                placeholder="Contractor Types"
                :options="contractorSubTypes"
                name="name"
                optionKey="id"
              />
            </a-form-model-item>
          </div>
        </div>
        <a-form-model-item :colon="false" label-align="left" label="Date">
          <a-date-picker v-model="form.date" placeholder="Signature Date" class="w-full" :format="visualDateFormat" />
        </a-form-model-item>
      </div>
    </a-form-model>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal"> Cancel </a-button>
        <a-button icon="save" type="primary" @click="handleBackfill" :loading="loadingBackfill"> Backfill </a-button>
      </div>
    </template></a-modal
  >
</template>

<script>
import SearchAssociates from '@/components/search-associates.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import { CONTRACTOR_SUB_TYPES } from '@/store/modules/legal-entity/constants'
import { deepCopy } from '@/utils/deep-copy'
import { normalizePaymentTypes as normalizePaymentTypeValues } from '@/utils/payment-types'
import { Button, Checkbox, DatePicker, FormModel, Modal, notification, Radio } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: documentActions } = createNamespacedHelpers('document')

const initialForm = {
  legal_entities: [],
  exclude_legal_entities: [],
  contractor_sub_types: [],
  exclude_contractor_sub_types: [],
  roles: [],
  exclude_roles: [],
  payment_types: [],
  exclude_payment_types: [],
  associates: [],
  date: undefined
}

export default {
  components: {
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-date-picker': DatePicker,
    'custom-select': CustomSelect,
    'search-associates': SearchAssociates
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    targetObject: {
      type: Object,
      default: undefined
    },
    specificAssociates: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      loadingBackfill: false,

      form: { ...initialForm },
      excludeRolesMode: false,
      excludeLegalEntitiesMode: false,
      isScopeHydrating: false,

      associateFilterType: 'all'
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.isScopeHydrating = true
          this.excludeRolesMode = false
          this.excludeLegalEntitiesMode = false
          this.associateFilterType = this.specificAssociates.length ? 'specific' : 'all'
          this.form.associates = deepCopy(this.specificAssociates)
          this.fillDefaultForm()
          this.isScopeHydrating = false
        }
      },
      immediate: true
    },
    'form.payment_types': {
      handler(newVal, oldVal) {
        if (this.isScopeHydrating) return

        const hasContractor = this.normalizePaymentTypes(newVal).includes('contractor')
        const hadContractor = this.normalizePaymentTypes(oldVal).includes('contractor')

        if (!hasContractor) {
          this.form.contractor_sub_types = []
          return
        }

        if (!hadContractor) {
          this.form.contractor_sub_types = this.contractorSubTypes.map(item => item.id)
        }
      },
      deep: true
    }
  },
  computed: {
    hasContractorPaymentType() {
      return this.paymentTypeProxy.includes('contractor')
    },
    paymentTypeProxy: {
      get() {
        return this.normalizePaymentTypes(this.form.payment_types)
      },
      set(value) {
        this.form.payment_types = this.normalizePaymentTypes(value)
      }
    },
    modalTitle() {
      return `Backfill ${this.targetObject?.name ?? ''}`
    },
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    legalEntities() {
      if (this.targetObject.scope?.exclude_legal_entities?.length) {
        const excluded = this.targetObject.scope.exclude_legal_entities.map(
          item => item.parent_entity ?? item.id ?? item
        )
        return this.$store.state.applicationState.projectLegalEntities.filter(
          item => !excluded.includes(item.parent_entity ?? item.id)
        )
      }
      return this.$store.state.applicationState.projectLegalEntities.filter(item =>
        this.targetObject.scope && this.targetObject.scope.legal_entities.length
          ? this.targetObject.scope.legal_entities
              .map(item => item.parent_entity ?? item.id)
              .includes(item.parent_entity ?? item.id)
          : true
      )
    },
    roles() {
      if (this.targetObject.scope?.exclude_roles?.length) {
        const excluded = this.targetObject.scope.exclude_roles.map(item => item.parent_entity ?? item.id ?? item)
        return this.$store.state.applicationState.projectRoles.filter(
          item => !excluded.includes(item.parent_entity ?? item.id)
        )
      }
      return this.$store.state.applicationState.projectRoles.filter(item =>
        this.targetObject.scope && this.targetObject.scope.roles.length
          ? this.targetObject.scope.roles
              .map(item => item.parent_entity ?? item.id)
              .includes(item.parent_entity ?? item.id)
          : true
      )
    },
    paymentTypes() {
      return this.$store.state.applicationState.paymentTypes
        .map(item => ({
          name: item.name,
          id: item.name.toLowerCase()
        }))
        .filter(item =>
          this.targetObject.scope && this.targetObject.scope.payment_types.length
            ? this.normalizePaymentTypes(this.targetObject.scope.payment_types).includes(item.id)
            : true
        )
    },
    contractorSubTypes() {
      return CONTRACTOR_SUB_TYPES.filter(item =>
        this.targetObject.scope && this.targetObject.scope.contractor_sub_types?.length
          ? this.targetObject.scope.contractor_sub_types.includes(item.id)
          : true
      )
    },

    roleProxy: {
      get() {
        const roles = this.excludeRolesMode ? this.form.exclude_roles : this.form.roles
        return roles ? roles.map(item => item.id) : undefined
      },
      set(value) {
        const mappedValue = value.map(item => ({ id: item }))
        if (this.excludeRolesMode) {
          this.form.exclude_roles = mappedValue
          this.form.roles = []
          return
        }
        this.form.roles = mappedValue
        this.form.exclude_roles = []
      }
    },

    agencyProxy: {
      get() {
        const legalEntities = this.excludeLegalEntitiesMode
          ? this.form.exclude_legal_entities
          : this.form.legal_entities
        return legalEntities || undefined
      },
      set(value) {
        if (this.excludeLegalEntitiesMode) {
          this.form.exclude_legal_entities = value
          this.form.legal_entities = []
          return
        }
        this.form.legal_entities = value
        this.form.exclude_legal_entities = []
      }
    },
    contractorSubTypeProxy: {
      get() {
        return this.form.contractor_sub_types?.length ? this.form.contractor_sub_types : undefined
      },
      set(value) {
        this.form.contractor_sub_types = value
      }
    }
  },
  methods: {
    ...documentActions(['backfillObject']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    handleExcludeRolesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeRolesMode) return

      this.excludeRolesMode = checked
      if (checked) {
        this.form.exclude_roles = [...this.form.roles]
        this.form.roles = []
        return
      }
      this.form.roles = [...this.form.exclude_roles]
      this.form.exclude_roles = []
    },
    handleExcludeLegalEntitiesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeLegalEntitiesMode) return

      this.excludeLegalEntitiesMode = checked
      if (checked) {
        this.form.exclude_legal_entities = [...this.form.legal_entities]
        this.form.legal_entities = []
        return
      }
      this.form.legal_entities = [...this.form.exclude_legal_entities]
      this.form.exclude_legal_entities = []
    },

    closeModal() {
      this.form = deepCopy(initialForm)
      this.associateFilterType = 'all'
      this.excludeRolesMode = false
      this.excludeLegalEntitiesMode = false
      this.$emit('update:visible', false)
    },

    fillDefaultForm() {
      this.form.payment_types = this.paymentTypes.map(item => item.id)
      this.form.exclude_payment_types = []

      this.form.legal_entities = this.legalEntities.map(item => item.id)
      this.form.exclude_legal_entities = []
      this.form.contractor_sub_types = this.hasContractorPaymentType ? this.contractorSubTypes.map(item => item.id) : []
      this.form.exclude_contractor_sub_types = []

      this.form.roles = this.roles
      this.form.exclude_roles = []
    },
    normalizeScope(scope) {
      return {
        ...scope,
        roles: this.excludeRolesMode ? [] : (scope.roles ?? []),
        exclude_roles: this.excludeRolesMode ? (scope.exclude_roles ?? []) : [],
        legal_entities: this.excludeLegalEntitiesMode ? [] : (scope.legal_entities ?? []),
        exclude_legal_entities: this.excludeLegalEntitiesMode ? (scope.exclude_legal_entities ?? []) : [],
        payment_types: this.normalizePaymentTypes(scope.payment_types),
        exclude_payment_types: scope.exclude_payment_types ?? [],
        contractor_sub_types: this.hasContractorPaymentType ? (scope.contractor_sub_types ?? []) : [],
        exclude_contractor_sub_types: scope.exclude_contractor_sub_types ?? []
      }
    },
    normalizePaymentTypes(paymentTypes = []) {
      return normalizePaymentTypeValues(paymentTypes, this.$store.state.applicationState.paymentTypes)
    },

    handleBackfill() {
      Modal.confirm({
        title: 'Want to Backfill?',
        content:
          'After backfilling, previous versions of the documents will no longer be available. Are you sure you want to proceed with the backfill?',
        onOk: async () => {
          this.loadingBackfill = true

          const payload = {
            object_type: this.type,
            object_id: this.targetObject.id,
            scope: {
              legal_entities: [],
              exclude_legal_entities: [],
              contractor_sub_types: [],
              exclude_contractor_sub_types: [],
              roles: [],
              exclude_roles: [],
              payment_types: [],
              exclude_payment_types: [],
              associates: []
            },
            date: this.form.date ? moment(this.form.date).utc().toISOString() : null
          }

          const { associates, date, ...rest } = this.form

          if (this.associateFilterType === 'specific') {
            payload.scope = {
              roles: [],
              exclude_roles: [],
              legal_entities: [],
              exclude_legal_entities: [],
              contractor_sub_types: [],
              exclude_contractor_sub_types: [],
              payment_types: [],
              exclude_payment_types: [],
              associates: []
            }
            payload.scope.associates = associates
          } else {
            payload.scope = this.normalizeScope(rest)
          }

          try {
            await this.backfillObject(payload)

            this.showNotification('success', 'Success', 'Backfill has been successfully done')
            this.$emit('add')

            this.closeModal()
          } catch (error) {
            this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
          } finally {
            this.loadingBackfill = false
          }
        },
        okText: 'Confirm',
        onCancel() {}
      })
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
